import type { CSSProperties } from "react";

type InputBannerKind =
  | "info"
  | "warning"
  | "error"
  | "rate-limit"
  | null
  | undefined;
type AttachmentTone =
  | "neutral"
  | "success"
  | "warning"
  | "error"
  | null
  | undefined;

type InputWellStyleOptions = {
  isPrototypeMinimalChrome: boolean;
  hasTypedQuestion?: boolean;
  panelTransitionStyle: CSSProperties;
};

type PrimaryActionClassNameOptions = {
  canStopActiveResponse: boolean;
  isPrototypeMinimalChrome: boolean;
};

type PrimaryActionStyleOptions = {
  isPrototypeMinimalChrome: boolean;
};

const inputBannerToneClassName = (kind: InputBannerKind): string => {
  if (kind === "rate-limit")
    return "border-red-200 bg-orange-50 text-orange-900";
  if (kind === "error") return "border-red-200 bg-red-50 text-red-800";
  if (kind === "warning") return "border-orange-200 bg-amber-50 text-orange-800";
  return "border-primary/12 bg-primary/5 text-irongrey";
};

const resolvedAttachmentToneClassName = (tone: AttachmentTone): string => {
  if (tone === "error") return "border-red-200 bg-red-50 text-red-800";
  if (tone === "warning") return "border-orange-200 bg-orange-50 text-orange-800";
  if (tone === "success") return "border-primary/12 bg-primary/5 text-primary";
  return "border-border bg-surface-light text-irongrey";
};

export const SCHOOL_SHELL_COMPOSER_STYLE_CONTRACT_TEST_IDS = [
  "ai-school-shell-composer-shell",
  "ai-school-shell-input-area",
  "ai-school-shell-attach-input",
  "ai-input-banner",
  "ai-rate-limit-banner",
  "ai-file-upload-chip-row",
  "ai-file-upload-chip",
  "ai-school-shell-composer-accessory",
  "ai-school-shell-attachment-chips",
  "ai-school-shell-attachment-chip",
  "ai-school-shell-attachment-remove",
  "ai-school-shell-grounding-attach",
  "ai-school-shell-input-well",
  "ai-input",
  "ai-school-shell-primary-action",
  "ai-school-shell-prototype-minimal-disclaimer",
] as const;

export const schoolShellComposerStyleContract = Object.freeze({
  composerShellClassName: "mx-auto flex w-full max-w-[720px] flex-col gap-1.5",
  attachInputClassName: "hidden",
  inputAreaClassName: (isPrototypeMinimalChrome: boolean): string =>
    `relative z-30 shrink-0 bg-surface-light/96 backdrop-blur ${isPrototypeMinimalChrome ? "px-4 pb-2.5 pt-2" : "px-4 pb-3 pt-2.5"}`,
  inputAreaStyle: (isPrototypeMinimalChrome: boolean): CSSProperties => ({
    minHeight: 0,
    paddingBottom: isPrototypeMinimalChrome
      ? "calc(0.875rem + env(safe-area-inset-bottom, 0px) + var(--ai-school-shell-keyboard-inset, 0px))"
      : "calc(0.75rem + env(safe-area-inset-bottom, 0px) + var(--ai-school-shell-keyboard-inset, 0px))",
  }),
  inputBannerClassName: (kind: InputBannerKind): string =>
    `rounded-[18px] border px-4 py-3 shadow-card ${inputBannerToneClassName(kind)}`,
  inputBannerContentClassName: "flex items-start justify-between gap-3",
  inputBannerBodyClassName: "min-w-0 flex-1",
  inputBannerTitleClassName: "text-[12px] font-semibold",
  inputBannerDescriptionClassName: "mt-1 text-[11px] leading-5 opacity-85",
  inputBannerActionsClassName: "flex shrink-0 items-center gap-2",
  inputBannerSecondaryActionClassName:
    "text-[11px] font-semibold opacity-80 hover:opacity-100",
  inputBannerPrimaryActionClassName:
    "rounded-full border border-current/15 bg-surface-light/60 px-3 py-1 text-[11px] font-semibold",
  fileUploadChipRowClassName: "flex flex-wrap gap-2",
  fileUploadChipClassName: (tone: AttachmentTone): string =>
    `inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold shadow-card ${resolvedAttachmentToneClassName(tone)}`,
  fileUploadChipLabelClassName: "truncate",
  fileUploadChipDetailClassName:
    "max-w-[180px] truncate text-[10px] font-medium opacity-70",
  composerAccessoryClassName: "space-y-2",
  stagedAttachmentRowClassName: "mb-1.5 flex flex-wrap gap-1",
  stagedAttachmentChipClassName:
    "inline-flex h-6 items-center gap-1 rounded-lg bg-muted pl-1 pr-1.5 text-[11px] text-irongrey",
  stagedAttachmentIconClassName: "text-text-secondary",
  stagedAttachmentLabelClassName: "max-w-[120px] truncate",
  stagedAttachmentRemoveClassName:
    "text-text-tertiary transition-colors hover:text-irongrey",
  controlRowClassName: (isPrototypeMinimalChrome: boolean): string =>
    `flex ${isPrototypeMinimalChrome ? "w-full items-center" : "gap-1.5 items-end"}`,
  groundingAttachClassName:
    "ml-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-text-secondary transition-colors hover:bg-background-light hover:text-primary",
  inputWellClassName: (isPrototypeMinimalChrome: boolean): string =>
    isPrototypeMinimalChrome
      ? "relative flex min-w-0 flex-1 items-center overflow-hidden border border-border bg-surface-light text-irongrey transition-colors focus-within:border-primary"
      : "relative flex flex-1 flex-col rounded-[18px] border border-border bg-surface-light px-4 py-3 shadow-card min-h-[104px]",
  inputWellStyle: ({
    hasTypedQuestion = true,
    isPrototypeMinimalChrome,
    panelTransitionStyle,
  }: InputWellStyleOptions): CSSProperties => ({
    ...(isPrototypeMinimalChrome
      ? {
          alignItems: "center",
          borderRadius: 24,
          boxSizing: "border-box",
          height: hasTypedQuestion ? undefined : 44,
          justifyContent: "center",
          maxHeight: 112,
          minHeight: 44,
          paddingLeft: 0,
          paddingRight: 0,
        }
      : null),
    ...panelTransitionStyle,
  }),
  inputClassName: (isPrototypeMinimalChrome: boolean): string =>
    `ai-school-shell-textarea w-full resize-none border-none bg-transparent text-irongrey outline-none shadow-none ring-0 focus:border-transparent focus:outline-none focus:shadow-none focus:ring-0 focus-visible:border-transparent focus-visible:outline-none focus-visible:shadow-none focus-visible:ring-0 disabled:text-text-tertiary placeholder:text-irongrey/55 disabled:placeholder:text-text-tertiary ${isPrototypeMinimalChrome ? "overflow-y-auto text-[13px]" : "pt-0 text-sm leading-6 min-h-[44px] pb-10 pr-10"}`,
  inputStyle: (
    isPrototypeMinimalChrome: boolean,
    hasTypedQuestion = true,
  ): CSSProperties | undefined =>
    isPrototypeMinimalChrome
      ? {
          height: hasTypedQuestion ? undefined : 42,
          lineHeight: "22px",
          maxHeight: 88,
          minHeight: 42,
          paddingBottom: 10,
          paddingLeft: 8,
          paddingRight: 42,
          paddingTop: 10,
          scrollbarWidth: "none",
        }
      : undefined,
  primaryActionClassName: ({
    canStopActiveResponse,
    isPrototypeMinimalChrome,
  }: PrimaryActionClassNameOptions): string =>
    [
      "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-0",
      canStopActiveResponse
        ? "bg-primary text-white shadow-soft hover:bg-primary-hover"
        : "bg-primary text-white shadow-soft hover:bg-primary-hover",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
      "disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-text-tertiary disabled:shadow-none",
      "absolute",
      isPrototypeMinimalChrome
        ? "z-10"
        : "bottom-3 right-3 mt-2",
    ].join(" "),
  primaryActionStyle: ({
    isPrototypeMinimalChrome,
  }: PrimaryActionStyleOptions): CSSProperties =>
    isPrototypeMinimalChrome
      ? {
          position: "absolute",
          right: 6,
          top: 6,
          zIndex: 10,
        }
      : {
          bottom: 12,
          position: "absolute",
          right: 12,
        },
  assistantAnimatedIconClassName: "inline-flex items-center justify-center",
  prototypeMinimalDisclaimerClassName:
    "w-full pt-1 text-center text-[9px] text-text-tertiary",
});
